import { v4 as uuidv4 } from 'uuid';
import cogoToast from 'cogo-toast';
import axios from 'axios';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import API_CONFIG from '../../config/api';

// Parse price to ensure it's a number
const parsePrice = (price) => {
  const numPrice = parseFloat(price);
  return isNaN(numPrice) ? 0 : numPrice;
};

// Async thunk for fetching cart items
export const fetchCartItems = createAsyncThunk(
  'cart/fetchCartItems',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_CONFIG.BASE_URL}/cart`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || 'Failed to fetch cart items'
      );
    }
  }
);

// Async thunk for adding item to cart
export const addItemToCart = createAsyncThunk(
  'cart/addItemToCart',
  async (cartData, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${API_CONFIG.BASE_URL}/addtocart`,
        cartData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
            'Content-Type': 'application/json',
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || 'Failed to add item to cart'
      );
    }
  }
);

// Async thunk for removing item from cart
export const removeItemFromCart = createAsyncThunk(
  'cart/removeItemFromCart',
  async (itemId, { rejectWithValue }) => {
    try {
      const response = await axios.delete(
        `${API_CONFIG.BASE_URL}/cart-items/${itemId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
          },
        }
      );
      return { itemId, response: response.data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data || 'Failed to remove item from cart'
      );
    }
  }
);

const cartSlice = createSlice({
  name: 'cart',
  initialState: {
    cartItems: [],
    loading: false,
    error: null,
  },
  reducers: {
    addToCart(state, action) {
      const { item, quantity = 1 } = action.payload;

      const existingItem = state.cartItems.find(
        (cartItem) =>
          cartItem.id === item.id &&
          (item.selectedProductSize
            ? cartItem.selectedProductSize === item.selectedProductSize
            : true) &&
          (item.selectedProductColor
            ? cartItem.selectedProductColor === item.selectedProductColor
            : true) &&
          (item.product_code_id
            ? cartItem.product_code_id === item.product_code_id
            : true)
      );

      if (existingItem) {
        existingItem.quantity += quantity;
      } else {
        state.cartItems.push({
          ...item,
          quantity: quantity,
          cartItemId: uuidv4(),
          price: parsePrice(item.price),
          image: item.image || [],
        });
      }

      cogoToast.success('Added To Cart', { position: 'bottom-left' });
    },
    deleteFromCart(state, action) {
      state.cartItems = state.cartItems.filter(
        (item) => item.cartItemId !== action.payload
      );
      cogoToast.error('Removed From Cart', { position: 'bottom-left' });
    },
    decreaseQuantity(state, action) {
      const item = action.payload;
      const existingItem = state.cartItems.find(
        (cartItem) =>
          cartItem.id === item.id &&
          (item.selectedProductSize
            ? cartItem.selectedProductSize === item.selectedProductSize
            : true) &&
          (item.selectedProductColor
            ? cartItem.selectedProductColor === item.selectedProductColor
            : true) &&
          (item.product_code_id
            ? cartItem.product_code_id === item.product_code_id
            : true)
      );

      if (existingItem && existingItem.quantity > 1) {
        existingItem.quantity -= 1;
      } else if (existingItem && existingItem.quantity === 1) {
        state.cartItems = state.cartItems.filter(
          (item) => item.cartItemId !== existingItem.cartItemId
        );
      }

      cogoToast.warn('Item Decremented From Cart', { position: 'bottom-left' });
    },
    deleteAllFromCart(state) {
      state.cartItems = [];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchCartItems.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchCartItems.fulfilled, (state, action) => {
      state.loading = false;

      if (action.payload.order && action.payload.order.order_items) {
        state.cartItems = action.payload.order.order_items.map((item) => ({
          cartItemId: item.id,
          id: item.product_id,
          quantity: item.quantity,
          price: parsePrice(item.product.price),
          name: item.product.name,
          image: item.product.images.map((img) => img.image_url),
          product_code_id: item.product_code_id,
          product_size_id: item.product_size_id,
          product_color_id: item.product_color_id,
          selectedProductSize: item.product_size_id,
          selectedProductColor: item.product_color_id,
        }));
      }
    });
    builder.addCase(fetchCartItems.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // Handle addItemToCart
    builder.addCase(addItemToCart.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(addItemToCart.fulfilled, (state, action) => {
      state.loading = false;

      // If we get a successful response, update the cart with the new item
      if (action.payload.order_item) {
        const newItem = action.payload.order_item;
        const product = action.payload.order.order_items[0].product;

        // Check if item already exists in cart
        const existingItemIndex = state.cartItems.findIndex(
          (item) =>
            item.id === newItem.product_id &&
            item.product_code_id === newItem.product_code_id
        );

        if (existingItemIndex >= 0) {
          // Update existing item
          state.cartItems[existingItemIndex].quantity = newItem.quantity;
        } else {
          // Add new item
          state.cartItems.push({
            cartItemId: newItem.id,
            id: newItem.product_id,
            quantity: newItem.quantity,
            price: parsePrice(product.price),
            name: product.name,
            image: product.images.map((img) => img.image_url),
            product_code_id: newItem.product_code_id,
            product_size_id: newItem.product_size_id,
            product_color_id: newItem.product_color_id,
            selectedProductSize: newItem.product_size_id,
            selectedProductColor: newItem.product_color_id,
          });
        }
      }

      cogoToast.success('Added To Cart', { position: 'bottom-left' });
    });
    builder.addCase(addItemToCart.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
      cogoToast.error('Failed to add item to cart', {
        position: 'bottom-left',
      });
    });

    // Handle removeItemFromCart
    builder.addCase(removeItemFromCart.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(removeItemFromCart.fulfilled, (state, action) => {
      state.loading = false;
      state.cartItems = state.cartItems.filter(
        (item) => item.cartItemId !== action.payload.itemId
      );
      cogoToast.error('Removed From Cart', { position: 'bottom-left' });
    });
    builder.addCase(removeItemFromCart.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
      cogoToast.error('Failed to remove item from cart', {
        position: 'bottom-left',
      });
    });
  },
});

export const {
  addToCart,
  deleteFromCart,
  decreaseQuantity,
  deleteAllFromCart,
} = cartSlice.actions;
export default cartSlice.reducer;
